import {
  ExpandLess,
  ExpandMore,
  Folder,
  FolderOpen,
  Source,
} from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Company } from "../types";
import { hostname, parseError } from "../utils";

// Helper function to get folder display properties
const getFolderDisplayProps = (
  folder: any,
  companyName: string
): {
  displayName: string;
  iconColor: "warning" | "primary" | "inherit";
  textColor?: string;
} => {
  const isSharedFolder = folder.name === "shared" && folder.path === "/";
  const isExternalRoot = folder.isExternalRoot === true;
  const isExternal = folder.isExternal === true;

  // Determine icon color based on shareVisibility
  let iconColor: "warning" | "primary" | "inherit" = "inherit";
  if (folder.shareVisibility === "public") {
    iconColor = "warning";
  } else if (folder.shareVisibility === "direct" || folder.shareVisibility === "company") {
    iconColor = "primary";
  }

  // Child folders under external root
  if (isExternal) {
    const displayName = folder.ownerCompany
      ? `${folder.name} (${folder.ownerCompany.name})`
      : folder.name;
    return {
      displayName,
      iconColor: "warning",
      textColor: "warning.main",
    };
  }
  // Company shared folder
  if (isSharedFolder) {
    return {
      displayName: companyName + " Shared",
      iconColor: "primary",
    };
  }
  // Return with shareVisibility-based color or inherit
  return { displayName: folder.name, iconColor };
};

export default ({
  getToken,
  eventListener,
  company,
  onValueChange,
  value = "/",
  title = "Knowledge-Base Path",
  sx = { mt: 2 },
}: {
  getToken: Function;
  eventListener: Function;
  company: Company;
  value: string;
  title?: string;
  onValueChange: (folderId: string) => void;
  sx?: any;
}) => {
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<any>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set()
  );

  const getFolders = async () => {
    setLoading(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/documents/folders`, {
        params: {
          companyId: company.id,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setFolders(res.data.folders);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDocumentChange = (e: any) => {
    const selectedPath = e.target.value;
    onValueChange(selectedPath);

    // Find the selected folder and expand it if it has children
    const findAndExpandFolder = (folderList: any[]): boolean => {
      for (const folder of folderList) {
        const folderPath = getValuePath(folder);
        if (
          folderPath === selectedPath &&
          folder.folders &&
          folder.folders.length > 0
        ) {
          setExpandedFolders((prev) => new Set(prev).add(folder.id));
          return true;
        }
        if (folder.folders && folder.folders.length > 0) {
          if (findAndExpandFolder(folder.folders)) return true;
        }
      }
      return false;
    };

    findAndExpandFolder(folders);
  };

  const toggleFolderExpansion = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const getValuePath = (doc: any) => {
    if (doc.path === "/") {
      return doc.path + doc.name;
    }
    return doc.path + "/" + doc.name;
  };

  useEffect(() => {
    if (company?.id) {
      getFolders();
    }
  }, [company?.id]);

  const renderFolders = (
    folderList: any[],
    level: number = 0
  ): React.ReactNode[] => {
    const items: React.ReactNode[] = [];

    folderList.forEach((folder) => {
      const hasChildren = folder.folders && folder.folders.length > 0;
      const isExpanded = expandedFolders.has(folder.id);
      const folderPath = getValuePath(folder);
      const { displayName, iconColor, textColor } = getFolderDisplayProps(
        folder,
        company.name
      );

      items.push(
        <MenuItem
          key={folder.id}
          value={folderPath}
          sx={{
            pl: 2 + level * 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" flex={1}>
            {hasChildren ? (
              isExpanded ? (
                <FolderOpen sx={{ mr: 1 }} color={iconColor} />
              ) : (
                <Folder sx={{ mr: 1 }} color={iconColor} />
              )
            ) : (
              <Source sx={{ mr: 1 }} color={iconColor} />
            )}
            <Typography sx={textColor ? { color: textColor } : undefined}>
              {displayName}
            </Typography>
          </Box>
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => toggleFolderExpansion(folder.id, e)}
              sx={{ ml: 1 }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </MenuItem>
      );

      if (hasChildren && isExpanded) {
        items.push(...renderFolders(folder.folders, level + 1));
      }
    });

    return items;
  };

  return (
    <FormControl fullWidth sx={sx}>
      <InputLabel>{title}</InputLabel>
      <Select
        value={value}
        label={title}
        onChange={handleDocumentChange}
        disabled={loading}
        renderValue={(selected) => {
          // Custom render for selected value to show only the path without chevron
          if (selected === "/") return "/";

          // Find the selected folder to get its display name
          const findSelectedFolder = (
            folderList: any[],
            targetPath: string
          ): any => {
            for (const folder of folderList) {
              if (getValuePath(folder) === targetPath) {
                return folder;
              }
              if (folder.folders && folder.folders.length > 0) {
                const found = findSelectedFolder(folder.folders, targetPath);
                if (found) return found;
              }
            }
            return null;
          };

          const selectedFolder = findSelectedFolder(
            folders,
            selected as string
          );
          if (selectedFolder) {
            const { displayName, iconColor, textColor } = getFolderDisplayProps(
              selectedFolder,
              company.name
            );
            const hasChildren =
              selectedFolder.folders && selectedFolder.folders.length > 0;

            return (
              <Box display="flex" alignItems="center">
                {hasChildren ? (
                  <Folder sx={{ mr: 1 }} color={iconColor} />
                ) : (
                  <Source sx={{ mr: 1 }} color={iconColor} />
                )}
                <Typography sx={textColor ? { color: textColor } : undefined}>
                  {displayName}
                </Typography>
              </Box>
            );
          }

          return selected;
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        <MenuItem key={"root"} value={"/"}>
          <Box display="flex" alignItems="center">
            <Source sx={{ mr: 1 }} />
            <Typography>/</Typography>
          </Box>
        </MenuItem>
        {loading ? (
          <MenuItem disabled>
            <Typography color="text.secondary">
              Loading knowledge base...
            </Typography>
          </MenuItem>
        ) : (
          renderFolders(folders)
        )}
      </Select>
    </FormControl>
  );
};
